import React from 'react';
import { Typography, Grid, makeStyles } from '@material-ui/core';
import { colors, moneyFormat } from 'design-system';

// Data Layer
import {
  Quotation,
  CoverageCatalog,
  CoverageCatalogResume,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import {
  coverageForCode,
  getDeductibleCoverages,
} from 'data-layer-cr/dist/functions/quotations';
import { deductibleCoverages } from 'data-layer-cr/dist/functions/coverages';

// Models
import CatalogItem from '../../../../../models/helperTypes/CatalogItem';
import PricingProfileResponse from '../../../../../models/responseTypes/PricingProfileResponse';
import { DefaultCard, DefaultCardRow } from '../../../../General';

const { formatMoney } = moneyFormat;

type Props = {
  quotation: Quotation;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  onSelectDeductibleOption: (value: string) => void;
  onToggleIncludeAllOptions: (newValue: boolean) => void;
  initialDeductibleOption: string;
  currentProfile: PricingProfileResponse;
};

const useStyles = makeStyles({
  coverageName: {
    fontSize: 15,
    fontWeight: 400,
    color: colors.abbey,
    letterSpacing: 0.1,
    fontStyle: 'normal',
  },
});

const possibleCollisionDeductibles: CatalogItem[] = [
  { Value: '1', Text: 'ASSA recomienda' },
  { Value: '2', Text: 'Medio' },
  { Value: '3', Text: 'Alto' },
];
export const DEFAULT_DEDUCTIBLE_OPTION = possibleCollisionDeductibles[0].Value;

const DynamicDeductibleCard = ({
  quotation,
  coverages,
  coverageResumes,
  currentProfile,
}: Props): JSX.Element => {
  const deductibleCoverageCatalogs = getDeductibleCoverages(
    quotation,
    coverageResumes,
  );

  const displayValues: React.ReactNode[] = [];
  const classes = useStyles();

  deductibleCoverages(coverages).forEach(
    (coverage: CoverageCatalog, index: number): void => {
      const curValue =
        deductibleCoverageCatalogs.filter(
          (cov: QuotationCoverage): boolean => cov.coverageId === coverage.code,
        )[0]?.deductible || 0;
      const foundCoverage = coverageForCode(
        deductibleCoverageCatalogs,
        coverage.code,
      );
      const usedPercentage = foundCoverage?.deductibleRate
        ? foundCoverage.deductibleRate
        : 10;
      const description = foundCoverage
        ? `${foundCoverage?.deductibleDescription}`
        : 'min fijo por evento';
      const needPercentage = Boolean(foundCoverage?.deductibleRate);
      displayValues.push(
        <DefaultCardRow
          key={coverage.code}
          displayItems={{
            left: (
              <Typography className={classes.coverageName}>
                {coverage.name}
              </Typography>
            ),
            right: (
              <Grid>
                <Typography
                  id={`sales-web-label-moneydeductible-${index}`}
                  variant="h2"
                  align="right"
                  style={{ fontSize: 23 }}
                >
                  {needPercentage
                    ? `${usedPercentage}%`
                    : `${formatMoney(
                        curValue,
                        false,
                        currentProfile.typeOfMoney,
                      )}`}
                </Typography>
                {needPercentage && (
                  <Typography
                    variant="subtitle2"
                    align="right"
                    data-test="sales-web-label-descriptionDeductible"
                  >
                    {description}
                  </Typography>
                )}
              </Grid>
            ),
          }}
        />,
      );
    },
  );

  if (displayValues.length <= 0) {
    return <div />;
  }

  return (
    <DefaultCard
      title="Deducibles"
      imageTitle={
        <img
          src="/svg/deducibles_car.svg"
          alt="deducibles"
          style={{ marginRight: 7, marginBottom: 15 }}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {displayValues}
        </Grid>
      </Grid>
    </DefaultCard>
  );
};

export default DynamicDeductibleCard;
